import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

// Create a no-op storage for server-side rendering
const noopStorage = {
  getItem: () => null,
  setItem: () => {},
  removeItem: () => {},
};

// Post-login redirect path lives in utils/auth/common/auth-storage.ts, not here — one source of truth.

interface ShowAlertNudgeStore {
  showAlertNudge: boolean;
  setShowAlertNudge: (newData: boolean) => void;
}

export const useShowAlertNudge = create<ShowAlertNudgeStore>()(
  persist(
    (set) => ({
      showAlertNudge: false,
      setShowAlertNudge: (newData: boolean) => {
        set({ showAlertNudge: newData });
      },
    }),
    {
      name: 'show-alert-nudge',
      storage: createJSONStorage(() => (typeof window !== 'undefined' ? localStorage : noopStorage)),
    }
  )
);

interface ShowLoginModalStore {
  showLoginModal: boolean;
  setShowLoginModal: (newData: boolean) => void;
}

export const useShowLoginModal = create<ShowLoginModalStore>()(
  persist(
    (set) => ({
      showLoginModal: false,
      setShowLoginModal: (newData: boolean) => {
        set({ showLoginModal: newData });
      },
    }),
    {
      name: 'show-login-modal',
      storage: createJSONStorage(() => (typeof window !== 'undefined' ? localStorage : noopStorage)),
    }
  )
);

/** Survey modal – when true, SurveyModalsManager opens the survey (e.g. after course/lesson complete). Aligned with academy-1.0. */
interface ShowSurveyStore {
  showSurvey: boolean;
  setShowSurvey: (newData: boolean) => void;
}

export const useSurveyStore = create<ShowSurveyStore>()(
  persist(
    (set) => ({
      showSurvey: false,
      setShowSurvey: (newData: boolean) => {
        set({ showSurvey: newData });
      },
    }),
    {
      name: 'show-survey',
      storage: createJSONStorage(() => (typeof window !== 'undefined' ? localStorage : noopStorage)),
    }
  )
);

/**
 * Shared "logout confirmation" modal state. MainNavigation and AnnouncementBar used to each
 * own a separate copy (own state + useLogout() + modal), which could desync and make
 * confirming logout silently no-op. One shared store + one modal instance instead
 * (see components/Logout/LogoutConfirmationController.tsx). Not persisted — transient UI state.
 */
interface LogoutConfirmStore {
  isOpen: boolean;
  isLoggingOut: boolean;
  open: () => void;
  close: () => void;
  setLoggingOut: (loggingOut: boolean) => void;
}

export const useLogoutConfirmStore = create<LogoutConfirmStore>()((set) => ({
  isOpen: false,
  isLoggingOut: false,
  open: () => set({ isOpen: true }),
  close: () => set({ isOpen: false, isLoggingOut: false }),
  setLoggingOut: (loggingOut: boolean) => set({ isLoggingOut: loggingOut }),
}));

interface LearningPathStore {
  learningPath: any;
  setLearningPath: (learningPath: any) => void;
}

export const useLearningPathStore = create<LearningPathStore>()(
  persist(
    (set) => ({
      learningPath: null,
      setLearningPath: (learningPath: any) => set({ learningPath: learningPath }),
    }),
    {
      name: 'learning-path-store',
      storage: createJSONStorage(() => (typeof window !== 'undefined' ? localStorage : noopStorage)),
    }
  )
);